import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../services/apiService';

const AuthContext = createContext(null);

function normalizeUser(userData) {
  if (!userData) return null;

  return {
    id: userData.id || userData._id,
    username: userData.username,
    email: userData.email,
    role: userData.role || 'user',
    status: userData.status || 'active',
    emailVerified: Boolean(userData.emailVerified),
    verified: Boolean(userData.verified),
    badges: Array.isArray(userData.badges) ? userData.badges : [],
    mustChangePassword: Boolean(userData.mustChangePassword),
    hasLocalAuth: Boolean(userData.hasLocalAuth),
    avatarUrl: userData.avatarUrl || null,
    discordId: userData.discordId || null,
    providers: userData.providers || [],
    profile: userData.profile || {},
    preferredLanguage: userData.preferredLanguage === 'en' ? 'en' : 'pt',
    canAccessFinance: Boolean(userData.canAccessFinance),
    systemAccount: {
      enabled: Boolean(userData.systemAccount?.enabled),
      autoFollow: Boolean(userData.systemAccount?.autoFollow),
      featured: Boolean(userData.systemAccount?.featured),
      coverUrl: userData.systemAccount?.coverUrl || null,
      operatingSince: userData.systemAccount?.operatingSince || null,
    },
    lastLoginAt: userData.lastLoginAt || null,
    createdAt: userData.createdAt || null,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyUser = useCallback((userData) => {
    const normalized = normalizeUser(userData);
    setUser(normalized);
    return normalized;
  }, []);

  const refreshUser = useCallback(async () => {
    const response = await authService.getCurrentUser();
    return applyUser(response?.data?.user || null);
  }, [applyUser]);

  useEffect(() => {
    let active = true;

    const bootstrap = async () => {
      try {
        const hasToken = Boolean(authService.getAccessToken() || localStorage.getItem('token'));
        if (!hasToken) {
          // Tenta renovar via cookie httpOnly de refresh (401 sem cookie é esperado)
          try {
            const refreshed = await authService.refresh();
            if (active) applyUser(refreshed?.data?.user || null);
            if (active && !refreshed?.data?.user) await refreshUser();
          } catch {
            if (active) {
              authService.clearSession();
              setUser(null);
            }
          }
          return;
        }

        const response = await authService.getCurrentUser();
        if (active) applyUser(response?.data?.user || null);
      } catch {
        try {
          const refreshed = await authService.refresh();
          if (active) applyUser(refreshed?.data?.user || null);
        } catch {
          if (active) {
            authService.clearSession();
            setUser(null);
          }
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    bootstrap();
    return () => {
      active = false;
    };
  }, [applyUser, refreshUser]);

  // Detecta reset forçado (e outras mudanças de conta) sem exigir logout/login
  useEffect(() => {
    if (loading || !user) return undefined;

    let active = true;

    const syncUser = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (!active) return;
        applyUser(response?.data?.user || null);
      } catch {
        // Mantém a sessão local; o interceptor trata 401/refresh
      }
    };

    const onFocus = () => {
      if (document.visibilityState === 'visible') syncUser();
    };

    // 2 min + focus/visibility; /auth/me não entra mais no rate limit global.
    const intervalId = window.setInterval(syncUser, 120_000);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [loading, user?.id, applyUser]);

  const login = useCallback(async (userData, token) => {
    if (token) authService.setAccessToken(token);
    if (userData) return applyUser(userData);
    return refreshUser();
  }, [applyUser, refreshUser]);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
  }, []);

  const logoutAll = useCallback(async () => {
    await authService.logoutAll();
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: Boolean(user),
    login,
    logout,
    logoutAll,
    refreshUser,
    updateUser: applyUser,
  }), [user, loading, login, logout, logoutAll, refreshUser, applyUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
